
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { submitPostReport } from '../services/moderationService';
import { useTranslation } from 'react-i18next';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  petName?: string;
}

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  postId,
  petName
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedReason, setSelectedReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const REPORT_REASONS = [
    { value: 'spam', label: t('modals.report.reasons.spam') },
    { value: 'harassment', label: t('modals.report.reasons.harassment') },
    { value: 'inappropriate', label: t('modals.report.reasons.inappropriate') },
    { value: 'misinformation', label: t('modals.report.reasons.misinformation') },
    { value: 'other', label: t('modals.report.reasons.other') }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !selectedReason) return;

    setIsSubmitting(true);

    try {
      await submitPostReport(postId, selectedReason, description);
      
      toast.success(t('modals.report.success'));
      onClose();
      setSelectedReason('');
      setDescription('');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(t('modals.report.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('modals.report.title')}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-3 block">
              {t('modals.report.question')}
            </Label>
            <RadioGroup
              value={selectedReason}
              onValueChange={setSelectedReason}
              className="space-y-2"
            >
              {REPORT_REASONS.map((reason) => (
                <div key={reason.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={reason.value} id={reason.value} />
                  <Label htmlFor={reason.value} className="text-sm">
                    {reason.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="description" className="text-sm font-medium">
              {t('modals.report.additionalDetails')}
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('modals.report.detailsPlaceholder')}
              className="mt-1"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!selectedReason || isSubmitting}
            >
              {isSubmitting ? t('modals.report.submitting') : t('modals.report.submitReport')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
